// ── BCHNostr — Podcast setup ───────────────────────────────────────
//
// Connecting a podcast lives HERE, on the BCH Podcasts page, next to the
// thing it creates — not buried in the profile form. A podcaster arrives
// at /podcasts, pastes their RSS feed, and their page builds itself.
//
// This component is fully self-contained: it owns its own form state,
// feed validation, and publishing. A podcast is a STANDALONE feature — it
// is not tied to the Professional Profile (kind 30499) in any way. Setting
// one up publishes only the show, and removing it retires only the show.
//
// Publishing writes a single event: the kind 30078 show event with the
// feed and metadata. The feed MUST validate first — a dead or mistyped
// feed can never be saved.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic2, RefreshCw, Loader2, CheckCircle, AlertTriangle, Save, Check,
  ChevronRight, X, Trash2, ExternalLink, Search, Sparkles, PenLine, ArrowLeft,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useBCHPodcast } from '../hooks/usePodcast';
import { uploadImage } from '../lib/imageUpload';
import {
  PODCAST_KIND, PODCAST_CATEGORIES, buildPodcastEvent, retirePodcastEvent,
  fetchFeed, clearFeedCache, refreshFeed, safeFeedUrl, podcastPath,
  type PodcastFeed,
} from '../lib/podcast';
import {
  resolvePodcast, platformLabel,
  type ResolveResult, type PodcastPlatform,
} from '../lib/podcastResolver';

type FeedState = 'idle' | 'checking' | 'valid' | 'invalid';
/** The discovery-first flow's high-level step. */
type SetupStep = 'discover' | 'confirm' | 'form';

// ── Cover image uploader ──────────────────────────────────────────
// A small local uploader so this component doesn't have to reach into
// SettingsPage for its image field. Most podcasters will let the RSS feed
// fill this in automatically anyway.

function CoverField({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(0);
    try {
      onChange(await uploadImage(file, setProgress));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div>
      <label className="text-[11px] font-medium text-muted-foreground">Cover Image</label>
      <div className="flex items-center gap-3 mt-1">
        {value ? (
          <img src={value} alt="" className="w-16 h-16 rounded-xl object-cover border border-border shrink-0" />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-muted border border-dashed border-border flex items-center justify-center shrink-0">
            <Mic2 className="w-5 h-5 text-muted-foreground/50" />
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-1.5">
          <input
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder="https://…/cover.jpg"
            className="w-full bg-background rounded-lg px-3 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="px-2.5 py-1 rounded-lg bg-muted text-[11px] font-medium hover:bg-muted/70 disabled:opacity-50"
            >
              {uploading ? `Uploading ${progress}%` : 'Upload'}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="p-1 rounded text-muted-foreground hover:text-red-500"
                title="Remove cover"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ── Setup panel ───────────────────────────────────────────────────

export default function PodcastSetup({ onDone }: { onDone?: () => void }) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const { podcast: existing, refetch } = useBCHPodcast(user?.pubkey);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState('');
  const [feedUrl, setFeedUrl] = useState('');
  const [website, setWebsite] = useState('');
  const [hosts, setHosts] = useState('');
  const [category, setCategory] = useState('bitcoin');
  const [bch, setBch] = useState('');
  const [guid, setGuid] = useState('');

  const [feedState, setFeedState] = useState<FeedState>('idle');
  const [feedMessage, setFeedMessage] = useState('');
  const [preview, setPreview] = useState<PodcastFeed | null>(null);

  // ── Discovery-first flow ──
  // A creator pastes ANY podcast link (Apple, Spotify, Amazon, a site, or a
  // raw feed) and we discover the underlying RSS feed. `step` gates which
  // panel shows: the URL box, the "podcast found" confirmation, or the full
  // editable form. Editing an already-connected show jumps straight to the
  // form.
  const [step, setStep] = useState<SetupStep>('discover');
  const [discoverUrl, setDiscoverUrl] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState('');
  const [discoverErrorAllowManual, setDiscoverErrorAllowManual] = useState(false);
  const [resolved, setResolved] = useState<ResolveResult | null>(null);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [removing, setRemoving] = useState(false);

  // Track which fields the creator has typed into, so relay hydration and
  // RSS auto-fill never clobber their own words.
  const touched = useRef<Set<string>>(new Set());
  const hydrated = useRef(false);

  // Hydrate from the existing show once. An already-published feed counts
  // as pre-validated so editing the category doesn't force a re-check.
  useEffect(() => {
    if (hydrated.current || !existing) return;
    hydrated.current = true;
    if (!touched.current.has('name')) setName(existing.name);
    if (!touched.current.has('description')) setDescription(existing.description);
    if (!touched.current.has('image')) setImage(existing.image);
    if (!touched.current.has('feedUrl')) setFeedUrl(existing.feedUrl);
    if (!touched.current.has('website')) setWebsite(existing.website);
    if (!touched.current.has('hosts')) setHosts(existing.hosts.join(', '));
    if (!touched.current.has('category')) setCategory(existing.category);
    if (!touched.current.has('bch')) setBch(existing.bch);
    setGuid(existing.guid);
    if (existing.feedUrl) {
      setFeedState('valid');
      setFeedMessage('Feed connected.');
      // An existing show is already set up — go straight to the editable form
      // rather than the discovery box.
      setStep('form');
    }
  }, [existing]);

  const mark = (field: string) => touched.current.add(field);

  /**
   * Discover the RSS feed from a pasted podcast URL.
   *
   * Runs the provider pipeline in lib/podcastResolver: identify platform →
   * extract show id → discover origin feed → validate → pull metadata. On
   * success we move to the confirmation screen; on failure we surface a
   * plain-language message and (where it makes sense) offer manual RSS entry.
   * We NEVER silently fail and never fabricate a feed URL.
   */
  const handleDiscover = useCallback(async () => {
    const input = discoverUrl.trim();
    if (!input) return;

    setDiscovering(true);
    setDiscoverError('');
    setDiscoverErrorAllowManual(false);
    setResolved(null);

    const result = await resolvePodcast(input);

    setDiscovering(false);

    if (!result.ok || !result.feed) {
      setDiscoverError(result.message);
      // 'invalid-url' is the only case where manual RSS entry wouldn't help;
      // every other failure gets the manual fallback the addendum requires.
      setDiscoverErrorAllowManual(result.error !== 'invalid-url');
      return;
    }

    // Success — stage the result for the confirmation screen.
    setResolved(result);
    setStep('confirm');
  }, [discoverUrl]);

  /**
   * Accept the discovered feed: copy its metadata into the editable form and
   * mark the feed as validated so publishing is unlocked. The creator lands
   * on the form pre-filled, free to tweak anything before publishing.
   */
  const handleUseResolved = useCallback(() => {
    if (!resolved || !resolved.feed) return;
    const { feed, feedUrl, publisher } = resolved;

    setFeedUrl(feedUrl);
    if (feed.title) setName(feed.title);
    if (feed.description) setDescription(feed.description);
    if (feed.image) setImage(feed.image);
    if (feed.link) setWebsite(feed.link);
    if (publisher || feed.author) setHosts(publisher || feed.author);
    if (feed.guid) setGuid(feed.guid);

    setPreview(feed);
    setFeedState('valid');
    setFeedMessage(
      feed.episodes.length > 0
        ? `Found ${feed.episodes.length} episode${feed.episodes.length === 1 ? '' : 's'}.`
        : 'Valid feed, but it has no episodes yet.',
    );
    setStep('form');
  }, [resolved]);

  /** Drop into manual RSS entry — the fallback that's always available. */
  const handleEnterManually = useCallback(() => {
    // Seed the manual field with whatever they pasted only if it already
    // looks like a feed URL; otherwise leave it blank so they can paste RSS.
    const maybeFeed = safeFeedUrl(discoverUrl);
    setFeedUrl(maybeFeed);
    setFeedState('idle');
    setFeedMessage('');
    setPreview(null);
    setDiscoverError('');
    setStep('form');
  }, [discoverUrl]);

  /**
   * Validate the feed and pre-fill blank fields from it.
   *
   * On demand rather than per-keystroke: a feed fetch goes through a proxy
   * and can take seconds. Nothing saves until this succeeds.
   */
  const handleCheckFeed = useCallback(async () => {
    const url = safeFeedUrl(feedUrl);
    if (!url) {
      setFeedState('invalid');
      setFeedMessage('Enter a full https:// RSS feed URL.');
      setPreview(null);
      return;
    }

    setFeedState('checking');
    setFeedMessage('');
    setPreview(null);

    const { feed, message } = await fetchFeed(url);
    if (!feed) {
      setFeedState('invalid');
      setFeedMessage(message);
      return;
    }

    setFeedState('valid');
    setPreview(feed);
    setFeedMessage(
      feed.episodes.length > 0
        ? `Found ${feed.episodes.length} episode${feed.episodes.length === 1 ? '' : 's'}.`
        : 'Valid feed, but it has no episodes yet.',
    );

    // Auto-fill is a convenience, never an override of the creator's input.
    if (!touched.current.has('name') && feed.title) setName(feed.title);
    if (!touched.current.has('description') && feed.description) setDescription(feed.description);
    if (!touched.current.has('image') && feed.image) setImage(feed.image);
    if (!touched.current.has('website') && feed.link) setWebsite(feed.link);
    if (!touched.current.has('hosts') && feed.author) setHosts(feed.author);
    if (feed.guid) setGuid(feed.guid);
  }, [feedUrl]);

  const handleSave = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    const url = safeFeedUrl(feedUrl);
    if (!url || feedState !== 'valid') {
      setSaveError('Check your RSS feed before saving.');
      return;
    }

    setSaving(true);
    setSaveError('');
    const now = Math.floor(Date.now() / 1000);

    try {
      // Publish the show — and only the show. A podcast is a standalone
      // feature and never touches the Professional Profile (kind 30499).
      // Changing the feed URL invalidates the old cache entry, otherwise
      // the previous show's episodes would linger.
      if (existing?.feedUrl && existing.feedUrl !== url) clearFeedCache(existing.feedUrl);

      const { tags, content } = buildPodcastEvent({
        name: name.trim() || user.name || 'Untitled podcast',
        description,
        image,
        feedUrl: url,
        website,
        hosts: hosts.split(',').map(h => h.trim()).filter(Boolean),
        category,
        bch: bch.trim() || undefined,
        guid: guid.trim() || undefined,
      });
      await publish({ kind: PODCAST_KIND, content, tags, created_at: now });

      // Warm the cache so the show page has episodes immediately.
      refreshFeed(url).catch(() => {});

      setSaved(true);
      refetch();
      setTimeout(() => setSaved(false), 4000);
      onDone?.();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save podcast');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Disconnect the podcast. Retires the show event only — nothing else on
   * the account is touched, because a podcast stands on its own.
   */
  const handleRemove = async () => {
    if (!user || !existing) return;
    setRemoving(true);
    setSaveError('');
    const now = Math.floor(Date.now() / 1000);
    try {
      const { tags, content } = retirePodcastEvent();
      await publish({ kind: PODCAST_KIND, content, tags, created_at: now });

      if (existing.feedUrl) clearFeedCache(existing.feedUrl);
      hydrated.current = false;
      setFeedUrl('');
      setFeedState('idle');
      setStep('discover');
      setResolved(null);
      setDiscoverUrl('');
      setOpen(false);
      refetch();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to disconnect podcast');
    } finally {
      setRemoving(false);
    }
  };

  // ── Logged out: an invitation, not a form ──
  if (!user) {
    return (
      <div className="mx-4 mt-3 rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 to-emerald-500/5 p-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
            <Mic2 className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold">Have a podcast?</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
              Paste a link to your show — Apple, Spotify, your site — and BCHNostr builds
              your podcast page: episodes, discussion, and BCH tips.
            </p>
          </div>
          <button
            onClick={() => setShowLoginDialog(true)}
            className="shrink-0 px-3.5 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
          >
            Log in
          </button>
        </div>
      </div>
    );
  }

  const isConnected = !!existing;
  const canSave = feedState === 'valid' && !saving;

  return (
    <div className={`mx-4 mt-3 rounded-2xl border-2 overflow-hidden transition-colors ${
      isConnected ? 'border-primary bg-primary/5' : 'border-primary/40 bg-gradient-to-br from-primary/10 to-emerald-500/5'
    }`}>
      {/* ── Collapsed header ── */}
      <div className="flex items-center gap-3 p-4">
        <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
          <Mic2 className="w-5 h-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold">
            {isConnected ? 'Your Podcast' : 'Connect your podcast'}
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
            {isConnected
              ? existing.name
              : 'Paste a link to your show — Apple, Spotify, Amazon, your site, or an RSS feed.'}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isConnected && !open && (
            <Link
              to={podcastPath(user.pubkey)}
              className="px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
            >
              View
            </Link>
          )}
          <button
            onClick={() => setOpen(v => !v)}
            className="px-3 py-2 rounded-lg bg-muted text-xs font-semibold hover:bg-muted/70 flex items-center gap-1"
          >
            {open ? <X className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {open ? 'Close' : isConnected ? 'Edit' : 'Set up'}
          </button>
        </div>
      </div>

      {/* ── Expanded body ── */}
      {open && step === 'discover' && (
        <DiscoverPanel
          url={discoverUrl}
          setUrl={v => { setDiscoverUrl(v); setDiscoverError(''); }}
          discovering={discovering}
          error={discoverError}
          allowManual={discoverErrorAllowManual}
          onDiscover={handleDiscover}
          onEnterManually={handleEnterManually}
        />
      )}

      {open && step === 'confirm' && resolved && resolved.feed && (
        <ConfirmPanel
          resolved={resolved}
          onConnect={handleUseResolved}
          onBack={() => { setStep('discover'); setResolved(null); }}
          onEnterManually={handleEnterManually}
        />
      )}

      {/* ── Expanded form ── */}
      {open && step === 'form' && (
        <div className="px-4 pb-4 space-y-3 border-t border-primary/15 pt-3">
          {!isConnected && (
            <button
              type="button"
              onClick={() => { setStep('discover'); }}
              className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="w-3 h-3" /> Back to podcast search
            </button>
          )}
          <p className="text-[10px] text-muted-foreground leading-relaxed">
            BCHNostr doesn’t host your audio — your existing podcast host keeps doing that.
            Your feed stays the source of truth, and new episodes appear here automatically.
          </p>

          {/* ── RSS feed (the only required field) ── */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">
              RSS Feed URL <span className="text-primary">*</span>
            </label>
            <div className="flex gap-2 mt-1">
              <input
                value={feedUrl}
                onChange={e => {
                  mark('feedUrl');
                  setFeedUrl(e.target.value);
                  // Any edit invalidates a previous check — you can't save a
                  // feed URL that hasn't been validated in its current form.
                  setFeedState('idle');
                  setFeedMessage('');
                  setPreview(null);
                }}
                placeholder="https://feeds.example.com/my-show.xml"
                maxLength={500}
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                className="flex-1 min-w-0 bg-background rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                type="button"
                onClick={handleCheckFeed}
                disabled={!feedUrl.trim() || feedState === 'checking'}
                className="shrink-0 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
              >
                {feedState === 'checking'
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />}
                {feedState === 'checking' ? 'Checking' : 'Check'}
              </button>
            </div>

            {feedState === 'valid' && (
              <p className="flex items-start gap-1.5 text-[10px] text-primary font-medium mt-1.5">
                <CheckCircle className="w-3 h-3 shrink-0 mt-0.5" />
                <span>{feedMessage}</span>
              </p>
            )}
            {feedState === 'invalid' && (
              <p className="flex items-start gap-1.5 text-[10px] text-red-500 font-medium mt-1.5">
                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                <span>{feedMessage}</span>
              </p>
            )}
            {feedState === 'idle' && (
              <p className="text-[10px] text-muted-foreground mt-1">
                Must be an <strong className="text-foreground">https</strong> feed. Check it before saving.
              </p>
            )}

            {/* Preview so the creator can confirm we found the right show */}
            {preview && preview.episodes.length > 0 && (
              <div className="mt-2 rounded-lg border border-border bg-muted/40 p-2.5">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  Latest episodes
                </p>
                <ul className="space-y-1">
                  {preview.episodes.slice(0, 3).map(ep => (
                    <li key={ep.guid} className="text-[11px] truncate flex items-center gap-1.5">
                      <Mic2 className="w-2.5 h-2.5 text-primary shrink-0" />
                      <span className="truncate">{ep.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className="text-[11px] font-medium text-muted-foreground">Podcast Name</label>
            <input
              value={name}
              onChange={e => { mark('name'); setName(e.target.value); }}
              placeholder={user.name || 'My Bitcoin Cash Show'}
              maxLength={200}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="text-[11px] font-medium text-muted-foreground">Description</label>
            <textarea
              value={description}
              onChange={e => { mark('description'); setDescription(e.target.value); }}
              placeholder="What your show is about…"
              rows={3}
              maxLength={3000}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <CoverField value={image} onChange={url => { mark('image'); setImage(url); }} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-medium text-muted-foreground">Website</label>
              <input
                value={website}
                onChange={e => { mark('website'); setWebsite(e.target.value); }}
                placeholder="myshow.com"
                maxLength={300}
                className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium text-muted-foreground">Category</label>
              <select
                value={category}
                onChange={e => { mark('category'); setCategory(e.target.value); }}
                className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {PODCAST_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-medium text-muted-foreground">Hosts</label>
            <input
              value={hosts}
              onChange={e => { mark('hosts'); setHosts(e.target.value); }}
              placeholder="Alice, Bob"
              maxLength={300}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">Comma-separated.</p>
          </div>

          <div>
            <label className="text-[11px] font-medium text-muted-foreground">
              Show Donation Address <span className="text-muted-foreground/60">(optional)</span>
            </label>
            <input
              value={bch}
              onChange={e => { mark('bch'); setBch(e.target.value); }}
              placeholder="bitcoincash:q…"
              maxLength={120}
              autoComplete="off"
              spellCheck={false}
              className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 font-mono focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Leave blank to use your normal BCH address for tips.
            </p>
          </div>

          {/* ── Save ── */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" />
                : saved ? <Check className="w-4 h-4" />
                : <Save className="w-4 h-4" />}
              {saving ? 'Publishing…' : saved ? 'Published!' : isConnected ? 'Save changes' : 'Publish podcast'}
            </button>
            {isConnected && (
              <button
                onClick={handleRemove}
                disabled={removing}
                className="px-3 py-2.5 rounded-lg border border-border text-xs font-semibold text-muted-foreground hover:text-red-500 hover:border-red-500/40 disabled:opacity-50"
                title="Disconnect this podcast"
              >
                {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>

          {feedState !== 'valid' && (
            <p className="text-[10px] text-muted-foreground text-center">
              Check your feed to enable publishing.
            </p>
          )}
          {saveError && <p className="text-[11px] text-red-500 text-center">{saveError}</p>}
          {saved && (
            <Link
              to={podcastPath(user.pubkey)}
              className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-primary hover:underline"
            >
              View your podcast page <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

// ── Discover panel ────────────────────────────────────────────────
//
// The creator's first screen: one box that accepts ANY podcast link. It
// deliberately doesn't say "RSS" front and centre — a podcaster shouldn't
// need to know what that is. Manual RSS entry is always one click away.

function DiscoverPanel({
  url, setUrl, discovering, error, allowManual, onDiscover, onEnterManually,
}: {
  url: string;
  setUrl: (v: string) => void;
  discovering: boolean;
  error: string;
  allowManual: boolean;
  onDiscover: () => void;
  onEnterManually: () => void;
}) {
  return (
    <div className="px-4 pb-4 space-y-3 border-t border-primary/15 pt-3">
      <div>
        <label className="text-[11px] font-medium text-muted-foreground">
          Podcast URL or RSS Feed <span className="text-primary">*</span>
        </label>
        <form
          onSubmit={e => { e.preventDefault(); if (!discovering) onDiscover(); }}
          className="flex gap-2 mt-1"
        >
          <input
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://podcasts.apple.com/…  ·  spotify.com/…  ·  RSS"
            maxLength={600}
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            disabled={discovering}
            className="flex-1 min-w-0 bg-background rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={!url.trim() || discovering}
            className="shrink-0 px-3.5 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            {discovering
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Search className="w-3.5 h-3.5" />}
            {discovering ? 'Finding…' : 'Find'}
          </button>
        </form>

        {discovering && (
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground mt-2">
            <Sparkles className="w-3 h-3 text-primary animate-pulse" />
            Finding podcast…
          </p>
        )}

        {!discovering && error && (
          <div className="mt-2 rounded-lg border border-red-500/30 bg-red-500/5 p-2.5">
            <p className="flex items-start gap-1.5 text-[11px] text-red-500 font-medium">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>{error}</span>
            </p>
            {allowManual && (
              <button
                type="button"
                onClick={onEnterManually}
                className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted text-[11px] font-semibold hover:bg-muted/70"
              >
                <PenLine className="w-3 h-3" /> Enter RSS Feed Manually
              </button>
            )}
          </div>
        )}

        {!discovering && !error && (
          <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
            Paste your show’s page from Apple Podcasts, Spotify, Amazon Music, your own
            website, or its RSS feed. We’ll find the feed and import your episodes.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onEnterManually}
        className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-primary"
      >
        <PenLine className="w-3 h-3" /> Or enter an RSS feed manually
      </button>
    </div>
  );
}

// ── Confirmation panel ────────────────────────────────────────────
//
// "🎙️ Podcast Found" — everything we discovered, laid out so the creator
// can confirm it's the right show BEFORE anything is published. The RSS feed
// URL is shown plainly: the source of truth is never hidden.

function ConfirmPanel({
  resolved, onConnect, onBack, onEnterManually,
}: {
  resolved: ResolveResult;
  onConnect: () => void;
  onBack: () => void;
  onEnterManually: () => void;
}) {
  const { feed, feedUrl, publisher, platform } = resolved;
  if (!feed) return null;

  return (
    <div className="px-4 pb-4 space-y-3 border-t border-primary/15 pt-3">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="w-3 h-3" /> Search again
      </button>

      <div className="flex items-center gap-2">
        <span className="text-lg">🎙️</span>
        <h4 className="text-sm font-bold">Podcast Found</h4>
        <span className="ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
          via {platformLabel(platform as PodcastPlatform)}
        </span>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-3">
        {feed.image ? (
          <img
            src={feed.image}
            alt=""
            loading="lazy"
            className="w-20 h-20 rounded-xl object-cover border border-border shrink-0"
          />
        ) : (
          <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Mic2 className="w-7 h-7 text-primary/50" />
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-1.5">
          <div>
            <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Podcast Name</p>
            <p className="text-sm font-bold leading-tight">{feed.title || 'Untitled podcast'}</p>
          </div>
          {(publisher || feed.author) && (
            <div>
              <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Publisher</p>
              <p className="text-[12px] font-medium leading-tight">{publisher || feed.author}</p>
            </div>
          )}
          <div>
            <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider">Episodes</p>
            <p className="text-[12px] font-medium">{feed.episodes.length}</p>
          </div>
        </div>
      </div>

      {feed.description && (
        <div>
          <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">Description</p>
          <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-3">{feed.description}</p>
        </div>
      )}

      <div>
        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">RSS Feed</p>
        <p className="text-[10px] font-mono text-muted-foreground break-all">{feedUrl}</p>
      </div>

      <p className="flex items-center gap-1.5 text-[11px] text-primary font-semibold">
        <CheckCircle className="w-3.5 h-3.5" /> RSS feed validated
      </p>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onConnect}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90"
        >
          <Check className="w-4 h-4" /> Connect Podcast
        </button>
      </div>

      <button
        type="button"
        onClick={onEnterManually}
        className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground hover:text-primary"
      >
        <PenLine className="w-3 h-3" /> Not right? Enter an RSS feed manually
      </button>
    </div>
  );
}
